import React, { createContext, useContext, useState, useEffect } from 'react';
import { platformPositionColors } from '../config/platformPositionColors';
import { useTheme } from '@mui/material';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const theme = useTheme();
  const [scoringType, setScoringType] = useState(() => {
    return localStorage.getItem("scoringType") || "ppr";
  });
  
  const [platform, setPlatform] = useState(() => {
    return localStorage.getItem("platform") || "sleeper";
  });

  const [draftId, setDraftId] = useState(() => {
    return localStorage.getItem("draftId") || "";
  });

  const [teamCount, setTeamCount] = useState(() => {
    return parseInt(localStorage.getItem("teamCount")) || 12;
  });

  const [roundCount, setRoundCount] = useState(() => {
    return parseInt(localStorage.getItem("roundCount")) || 15;
  });

  // Roster format: Superflex (2QB)
  const [isSuperflex, setIsSuperflex] = useState(() => {
    try {
      const saved = localStorage.getItem("isSuperflex");
      return saved ? JSON.parse(saved) : false;
    } catch (_) {
      return false;
    }
  });

  // New state for favorites and custom draft assignments
  const [favoritePlayerIds, setFavoritePlayerIds] = useState(() => {
    const saved = localStorage.getItem("favoritePlayerIds");
    return saved ? JSON.parse(saved) : [];
  });

  const [customDraftAssignments, setCustomDraftAssignments] = useState(() => {
    const saved = localStorage.getItem("customDraftAssignments");
    return saved ? JSON.parse(saved) : {};
  });

  // Bump this to signal a draftboard reset to interested components
  const [resetDraftboardVersion, setResetDraftboardVersion] = useState(() => {
    const saved = localStorage.getItem("resetDraftboardVersion");
    return saved ? parseInt(saved) : 0;
  });

  const [lastResetOptions, setLastResetOptions] = useState(() => {
    try {
      const saved = localStorage.getItem("lastResetOptions");
      return saved ? JSON.parse(saved) : { resetDrafted: true, resetKeepers: false };
    } catch (_) {
      return { resetDrafted: true, resetKeepers: false };
    }
  });

  const [recentDraftIds, setRecentDraftIds] = useState(() => {
    const saved = localStorage.getItem("recentDraftIds");
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedLeagueId, setSelectedLeagueId] = useState(() => {
    return localStorage.getItem("selectedLeagueId") || "";
  });

  // New state for primary league preference and last used league
  const [primaryLeagueId, setPrimaryLeagueId] = useState(() => {
    return localStorage.getItem("primaryLeagueId") || "";
  });

  const [lastUsedLeagueId, setLastUsedLeagueId] = useState(() => {
    return localStorage.getItem("lastUsedLeagueId") || "";
  });

  // Live draft status and last picks update timestamp (for badges)
  const [liveDraftStatus, setLiveDraftStatus] = useState(() => {
    return localStorage.getItem("liveDraftStatus") || "";
  });
  const [lastPicksUpdatedAt, setLastPicksUpdatedAt] = useState(() => {
    const saved = localStorage.getItem("lastPicksUpdatedAt");
    return saved ? parseInt(saved) : null;
  });

  // Update localStorage when settings change
  useEffect(() => {
    localStorage.setItem("scoringType", scoringType);
  }, [scoringType]);

  useEffect(() => {
    localStorage.setItem("platform", platform);
  }, [platform]);

  useEffect(() => {
    localStorage.setItem("draftId", draftId);
  }, [draftId]);

  useEffect(() => {
    localStorage.setItem("teamCount", teamCount.toString());
  }, [teamCount]);

  useEffect(() => {
    localStorage.setItem("roundCount", roundCount.toString());
  }, [roundCount]);

  useEffect(() => {
    try {
      localStorage.setItem("isSuperflex", JSON.stringify(isSuperflex));
    } catch (_) {}
  }, [isSuperflex]);

  useEffect(() => {
    localStorage.setItem("favoritePlayerIds", JSON.stringify(favoritePlayerIds));
  }, [favoritePlayerIds]);

  useEffect(() => {
    localStorage.setItem("customDraftAssignments", JSON.stringify(customDraftAssignments));
  }, [customDraftAssignments]);

  useEffect(() => {
    localStorage.setItem("resetDraftboardVersion", String(resetDraftboardVersion));
  }, [resetDraftboardVersion]);

  useEffect(() => {
    try {
      localStorage.setItem("lastResetOptions", JSON.stringify(lastResetOptions));
    } catch (_) {}
  }, [lastResetOptions]);

  useEffect(() => {
    localStorage.setItem("recentDraftIds", JSON.stringify(recentDraftIds));
  }, [recentDraftIds]);

  useEffect(() => {
    localStorage.setItem("selectedLeagueId", selectedLeagueId);
    // When a league is selected, also update it as the last used league
    if (selectedLeagueId && selectedLeagueId !== lastUsedLeagueId) {
      setLastUsedLeagueId(selectedLeagueId);
    }
  }, [selectedLeagueId, lastUsedLeagueId]);

  useEffect(() => {
    localStorage.setItem("primaryLeagueId", primaryLeagueId);
  }, [primaryLeagueId]);

  useEffect(() => {
    localStorage.setItem("lastUsedLeagueId", lastUsedLeagueId);
  }, [lastUsedLeagueId]);

  useEffect(() => {
    if (liveDraftStatus) {
      localStorage.setItem("liveDraftStatus", liveDraftStatus);
    } else {
      localStorage.removeItem("liveDraftStatus");
    }
  }, [liveDraftStatus]);

  useEffect(() => {
    if (lastPicksUpdatedAt) {
      localStorage.setItem("lastPicksUpdatedAt", String(lastPicksUpdatedAt));
    } else {
      localStorage.removeItem("lastPicksUpdatedAt");
    }
  }, [lastPicksUpdatedAt]);

  const handleScoringTypeChange = (newScoringType) => {
    setScoringType(newScoringType);
  };

  const handlePlatformChange = (newPlatform) => {
    setPlatform(newPlatform);
  };

  const handleDraftIdChange = (newDraftId) => {
    setDraftId(newDraftId);
    
    // Add to recent draft IDs if it's not empty and not already in the list
    if (newDraftId && !recentDraftIds.includes(newDraftId)) {
      setRecentDraftIds(prev => {
        const updated = [newDraftId, ...prev.filter(id => id !== newDraftId)];
        // Keep only the 10 most recent
        return updated.slice(0, 10);
      });
    }
  };

  const handleTeamCountChange = (newTeamCount) => {
    setTeamCount(newTeamCount);
  };

  const handleRoundCountChange = (newRoundCount) => {
    setRoundCount(newRoundCount);
  };

  const handleSuperflexChange = (newIsSuperflex) => {
    setIsSuperflex(Boolean(newIsSuperflex));
  };

  // Handlers for favorites and custom draft assignments
  const toggleFavoritePlayer = (playerId) => {
    setFavoritePlayerIds(prev => {
      const updated = prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId];
      return updated;
    });
  };

  const assignPlayerDraftPosition = (playerId, round, position) => {
    console.log(`🟢 SettingsContext.assignPlayerDraftPosition: playerId=${playerId}, round=${round}, position=${position}`);
    setCustomDraftAssignments(prev => {
      const updated = {
        ...prev,
        [playerId]: { round, position, timestamp: Date.now() }
      };
      console.log(`🟢 SettingsContext.assignPlayerDraftPosition: Updated assignments:`, updated);
      return updated;
    });
  };

  const removePlayerDraftAssignment = (playerId) => {
    setCustomDraftAssignments(prev => {
      const updated = { ...prev };
      delete updated[playerId];
      return updated;
    });
  };

  const clearAllCustomAssignments = () => {
    setCustomDraftAssignments({});
  };

  // Global reset: clears drafted state UI and optionally keeper assignments
  const resetDraftboard = (options = { resetDrafted: true, resetKeepers: false }) => {
    const resetDrafted = Boolean(options?.resetDrafted);
    const resetKeepers = Boolean(options?.resetKeepers);
    if (resetKeepers) {
      setCustomDraftAssignments({});
    }
    setLastResetOptions({ resetDrafted, resetKeepers });
    setResetDraftboardVersion((v) => v + 1);
  };

  const removeRecentDraftId = (draftIdToRemove) => {
    setRecentDraftIds(prev => prev.filter(id => id !== draftIdToRemove));
  };

  const handleSelectedLeagueChange = (leagueId) => {
    setSelectedLeagueId(leagueId);
  };

  const handlePrimaryLeagueChange = (leagueId) => {
    setPrimaryLeagueId(leagueId);
  };

  // Enhanced function to get the best available league ID based on priority
  const getBestAvailableLeagueId = (availableLeagues = []) => {
    // Priority order: 1) Primary league, 2) Last used league, 3) Selected league, 4) First available league
    const candidates = [
      primaryLeagueId,
      lastUsedLeagueId, 
      selectedLeagueId,
      availableLeagues.length > 0 ? availableLeagues[0].id : null
    ].filter(Boolean); // Remove empty values
    
    // Return the first valid candidate that exists in available leagues
    for (const candidate of candidates) {
      if (candidate && (availableLeagues.length === 0 || availableLeagues.some(league => league.id === candidate))) {
        return candidate;
      }
    }
    
    return null;
  };

  // Get current platform's position colors based on theme mode
  const getCurrentPositionColors = () => {
    const platformColors = platformPositionColors[platform];
    
    if (!platformColors) {
      // Fallback to sleeper if platform doesn't exist
      return platformPositionColors.sleeper.light;
    }
    
    // All platforms now have light/dark structure
    const themeMode = theme.palette.mode;
    return platformColors[themeMode] || platformColors.light;
  };

  // Memoize the position colors to update when platform or theme changes
  // getCurrentPositionColors is recreated every render, so listing it as a dependency
  // would invalidate this memo on every render and defeat its purpose. It only reads
  // `platform` and the theme mode, both of which are already dependencies.
  const positionColors = React.useMemo(() => {
    return getCurrentPositionColors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, theme.palette.mode]);

  const value = {
    scoringType,
    platform,
    draftId,
    teamCount,
    roundCount,
    isSuperflex,
    favoritePlayerIds,
    customDraftAssignments,
    recentDraftIds,
    handleScoringTypeChange,
    handlePlatformChange,
    handleDraftIdChange,
    handleTeamCountChange,
    handleRoundCountChange,
    handleSuperflexChange,
    toggleFavoritePlayer,
    assignPlayerDraftPosition,
    removePlayerDraftAssignment,
    clearAllCustomAssignments,
    removeRecentDraftId,
    resetDraftboard,
    resetDraftboardVersion,
    lastResetOptions,
    positionColors,
    liveDraftStatus,
    setLiveDraftStatus,
    lastPicksUpdatedAt,
    setLastPicksUpdatedAt,
    selectedLeagueId,
    handleSelectedLeagueChange,
    primaryLeagueId,
    handlePrimaryLeagueChange,
    lastUsedLeagueId,
    getBestAvailableLeagueId,
    platformOptions: Object.keys(platformPositionColors),
    teamCountOptions: [8, 10, 12, 14, 16],
    roundCountOptions: [15, 16, 17, 18, 19, 20],
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}; 